import { useState, useEffect, useCallback } from 'react';
import * as analytics from '@/lib/analytics';
import { streakMilestone } from '@/lib/analyticsContext';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface StreakData {
  current_streak: number;
  longest_streak: number;
  last_check_in_date: string | null;
  total_check_ins: number;
}

export const useWellnessStreak = () => {
  const { user } = useAuth();
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);

  const fetchStreak = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching streak:', error);
        return;
      }

      if (data) {
        setStreakData({
          current_streak: data.current_streak,
          longest_streak: data.longest_streak,
          last_check_in_date: data.last_check_in_date,
          total_check_ins: data.total_check_ins,
        });

        // Check if user has checked in today (using local timezone)
        const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local tz
        setHasCheckedInToday(data.last_check_in_date === today);
      } else {
        setStreakData({
          current_streak: 0,
          longest_streak: 0,
          last_check_in_date: null,
          total_check_ins: 0,
        });
        setHasCheckedInToday(false);
      }
    } catch (error) {
      console.error('Error fetching streak:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStreak();
    const onUpdated = () => { void fetchStreak(); };
    window.addEventListener('vent-streak-updated', onUpdated);
    return () => window.removeEventListener('vent-streak-updated', onUpdated);
  }, [fetchStreak]);

  const updateStreak = useCallback(async () => {
    if (!user) return null;

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const { data, error } = await supabase
        .rpc('record_vent_streak_activity', { _timezone: timezone });
      if (!error) window.dispatchEvent(new Event('vent-streak-updated'));

      if (error) {
        console.error('Error updating streak:', error);
        return null;
      }

      if (data) {
        const updatedStreak = {
          current_streak: data.current_streak,
          longest_streak: data.longest_streak,
          last_check_in_date: data.last_check_in_date,
          total_check_ins: data.total_check_ins,
        };
        const reached = streakMilestone(streakData?.current_streak ?? 0, data.current_streak);
        if (reached) analytics.track('wellness_streak_milestone', { streak_days: reached, source_activity: 'check_in' });
        setStreakData(updatedStreak);
        setHasCheckedInToday(true);
        return updatedStreak;
      }

      return null;
    } catch (error) {
      console.error('Error updating streak:', error);
      return null;
    }
  }, [user]);

  return {
    streakData,
    loading,
    hasCheckedInToday,
    updateStreak,
    refetch: fetchStreak,
  };
};
